"use client";

import React, { useState, useEffect } from "react";
import {
  Send,
  CheckCircle,
  Users,
  Check,
  X,
  Wallet,
  DollarSign,
  AlertCircle,
  Star,
  Trash2,
  Pencil,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getBalance } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { useWallet } from "@crossmint/client-sdk-react-ui";

interface User {
  uid: string;
  email: string | null;
  phone: string | null;
  wallet: string;
  username: string;
  joinedAt: string;
  photoURL?: string | null;
}

interface Recipient {
  id: string;
  username?: string;
  email?: string | null;
  phone?: string | null;
  wallet: string;
  photoURL?: string | null;
}

interface PaymentRequest {
  id: string;
  requesterUid: string;
  requestedUid: string;
  requesterUsername: string;
  requestedUsername: string;
  requesterWallet: string;
  requestedWallet: string;
  requesterEmail: string | null;
  requestedEmail: string | null;
  amount: number;
  status: "pending" | "completed" | "cancelled";
  timestamp: string;
  targetPhotoURL?: string | null; // renamed for clarity (photo of the other party)
}

interface Favorite {
  id: string;
  type: "user";
  name: string;
  wallet: string;
  email?: string | null;
  phone?: string | null;
}

interface AddFavorite {
  type: "user";
  wallet: string;
  email?: string | null;
  phone?: string | null;
}

const PaymentRequestPage = () => {
  const [activeTab, setActiveTab] = useState<
    "send" | "myRequests" | "received"
  >("send");
  const [amount, setAmount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(
    null,
  );
  const [users, setUsers] = useState<Recipient[]>([]);
  const [filteredResults, setFilteredResults] = useState<Recipient[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sentRequests, setSentRequests] = useState<PaymentRequest[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<PaymentRequest[]>(
    [],
  );
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedPayRequest, setSelectedPayRequest] =
    useState<PaymentRequest | null>(null);
  const { wallet } = useWallet();

  const user = useSelector((state: RootState) => state.user) as User | null;
  const senderWallet = user?.wallet || "";
  const senderUid = user?.uid || "";
  const senderUsername = user?.username || "User";
  const senderEmail = user?.email || null;

  // Favorites
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [favoritesSearch, setFavoritesSearch] = useState("");
  const [showAddFavoriteModal, setShowAddFavoriteModal] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<AddFavorite | null>(null);
  const [customName, setCustomName] = useState("");
  const [showEditFavoriteModal, setShowEditFavoriteModal] = useState(false);
  const [selectedToEdit, setSelectedToEdit] = useState<Favorite | null>(null);
  const [editName, setEditName] = useState("");

  const sendEmail = async (
    endpoint: string,
    payload: {
      sender_email: string;
      receiver_email?: string | null;
      sender_name: string;
      receiver_name: string;
      params?: { [key: string]: string };
      transaction_details?: { [key: string]: string };
    },
  ) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to send ${endpoint} email (HTTP ${response.status})`,
        );
      }

      console.log(`${endpoint} email sent successfully`);
    } catch (err: any) {
      console.error(`Failed to send ${endpoint} email:`, err);
      toast.error(`Email failed: ${err.message}`);
    }
  };

  useEffect(() => {
    const fetchBalances = async () => {
      if (!senderWallet) {
        setError("No sender wallet address found.");
        toast.error("No sender wallet address found.");
        return;
      }

      setLoading(true);
      try {
        const usdcBal = await getBalance(senderWallet);
        setWalletBalance(usdcBal);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching balances:", err.message);
        toast.error("Failed to fetch balances.");
        setError("Failed to fetch balances: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();
  }, [senderWallet]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          username: doc.data().username,
          email: doc.data().email || null,
          phone: doc.data().phone || null,
          wallet: doc.data().wallet,
          photoURL: doc.data().photoURL || null,
        }));
        setUsers(usersData);
      } catch (err: any) {
        console.error("Error fetching users:", err);
        toast.error("Failed to fetch users.");
        setError("Failed to fetch users: " + err.message);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (!senderUid) return;

    const loadFavorites = async () => {
      try {
        const favoritesCollection = collection(
          db,
          "users",
          senderUid,
          "favorites",
        );
        const q = query(favoritesCollection, where("type", "==", "user"));
        const favoritesSnapshot = await getDocs(q);
        const favList: Favorite[] = favoritesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Favorite[];
        setFavorites(favList);
      } catch (err) {
        console.error("Error loading favorites:", err);
      }
    };
    loadFavorites();
  }, [senderUid]);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!senderUid) return;

      try {
        // Sent requests ("My Requests") → photo of the person you're requesting FROM (requestedUid)
        const sentQuery = query(
          collection(db, "paymentRequests"),
          where("requesterUid", "==", senderUid),
        );
        const sentSnapshot = await getDocs(sentQuery);
        const sentData = sentSnapshot.docs.map((doc) => {
          const data = doc.data();
          const targetUser = users.find((u) => u.id === data.requestedUid);
          return {
            id: doc.id,
            ...data,
            targetPhotoURL: targetUser?.photoURL || null,
          } as PaymentRequest;
        });
        setSentRequests(sentData);

        // Received requests → photo of the person requesting payment FROM you (requesterUid)
        const receivedQuery = query(
          collection(db, "paymentRequests"),
          where("requestedUid", "==", senderUid),
        );
        const receivedSnapshot = await getDocs(receivedQuery);
        const receivedData = receivedSnapshot.docs.map((doc) => {
          const data = doc.data();
          const requester = users.find((u) => u.id === data.requesterUid);
          return {
            id: doc.id,
            ...data,
            targetPhotoURL: requester?.photoURL || null,
          } as PaymentRequest;
        });
        setReceivedRequests(receivedData);
      } catch (err: any) {
        console.error("Error fetching payment requests:", err);
        toast.error("Failed to fetch payment requests.");
      }
    };

    fetchRequests();
  }, [senderUid, users]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedRecipient(null);

    const searchTerm = value.toLowerCase();
    const filtered = users.filter(
      (item) =>
        ((item.username?.toLowerCase() || "").includes(searchTerm) ||
          (item.email?.toLowerCase() || "").includes(searchTerm) ||
          (item.phone?.toLowerCase() || "").includes(searchTerm) ||
          (item.wallet?.toLowerCase() || "").includes(searchTerm)) &&
        item.id !== senderUid,
    );

    setFilteredResults(filtered);
    setShowDropdown(value.length >= 2 && filtered.length > 0);
  };

  const handleSelectRecipient = (selected: Recipient) => {
    setSelectedRecipient(selected);
    setSearchQuery(selected.username || "");
    setShowDropdown(false);
  };

  const maskContact = (contact?: string | null): string | null => {
    if (!contact) return null;
    if (contact.includes("@")) {
      const [local, domain] = contact.split("@");
      if (local.length <= 2) return contact;
      const maskedLocal = local.slice(0, 2) + "***";
      return `${maskedLocal}@${domain}`;
    }
    if (/^\+?\d{7,15}$/.test(contact.replace(/\s/g, ""))) {
      const digits = contact.replace(/\D/g, "");
      if (digits.length <= 4) return contact;
      const visible = digits.slice(-4);
      const masked = "***".repeat(digits.length - 4);
      return contact.replace(digits, masked + visible);
    }
    return contact;
  };

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const isFavorited = favorites.some(
    (fav) =>
      (fav.wallet || "").toLowerCase() ===
      (selectedRecipient?.wallet || "").toLowerCase(),
  );

  const addToFavorites = () => {
    if (!senderUid || !selectedRecipient) {
      toast.error("Please select a CashTap user first");
      return;
    }

    if (
      favorites.some(
        (f) =>
          (f.wallet || "").toLowerCase() ===
          selectedRecipient.wallet.toLowerCase(),
      )
    ) {
      toast("Already in favorites");
      return;
    }

    setSelectedToAdd({
      type: "user",
      wallet: selectedRecipient.wallet,
      email: selectedRecipient.email,
      phone: selectedRecipient.phone,
    });
    setCustomName(selectedRecipient.username || "");
    setShowAddFavoriteModal(true);
  };

  const confirmAddFavorite = async () => {
    if (!selectedToAdd || !customName.trim() || !senderUid) {
      toast.error("Name required");
      return;
    }

    try {
      const favoritesRef = collection(db, "users", senderUid, "favorites");
      await addDoc(favoritesRef, {
        ...selectedToAdd,
        name: customName.trim(),
      });

      toast.success("Added to favorites!");

      const snap = await getDocs(favoritesRef);
      const updated: Favorite[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Favorite[];
      setFavorites(updated);

      setShowAddFavoriteModal(false);
      setSelectedToAdd(null);
    } catch (err) {
      console.error(err);
      toast.error("Could not add to favorites");
    }
  };

  const confirmEditFavorite = async () => {
    if (!selectedToEdit || !editName.trim() || !senderUid) {
      toast.error("Name required");
      return;
    }

    try {
      await updateDoc(
        doc(db, "users", senderUid, "favorites", selectedToEdit.id),
        {
          name: editName.trim(),
        },
      );

      toast.success("Updated favorite!");

      const favoritesRef = collection(db, "users", senderUid, "favorites");
      const snap = await getDocs(favoritesRef);
      const updated: Favorite[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Favorite[];
      setFavorites(updated);

      setShowEditFavoriteModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not update favorite");
    }
  };

  const removeFavorite = async (favId: string) => {
    if (!senderUid) return;
    try {
      await deleteDoc(doc(db, "users", senderUid, "favorites", favId));
      toast.success("Removed from favorites");
      setFavorites((prev) => prev.filter((f) => f.id !== favId));
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove favorite");
    }
  };

  const selectFromFavorites = (fav: Favorite) => {
    const match = users.find(
      (r) =>
        (r.wallet || "").toLowerCase() === (fav.wallet || "").toLowerCase(),
    );

    let effectiveRecipient: Recipient;
    if (match) {
      effectiveRecipient = match;
      setSearchQuery(match.username || "");
    } else {
      effectiveRecipient = {
        id: fav.id || "fav-" + (fav.wallet || "").slice(-6),
        wallet: fav.wallet || "",
        username: fav.name,
        email: fav.email,
        phone: fav.phone,
      };
      setSearchQuery(fav.name);
    }

    setSelectedRecipient(effectiveRecipient);
    setShowFavoritesModal(false);
  };

  const filteredFavorites = favorites.filter((fav) => {
    if (!favoritesSearch.trim()) return true;
    const term = favoritesSearch.toLowerCase();
    return (
      (fav.name || "").toLowerCase().includes(term) ||
      (fav.wallet || "").toLowerCase().includes(term) ||
      (fav.email || "").toLowerCase().includes(term) ||
      (fav.phone || "").toLowerCase().includes(term)
    );
  });

  const handleSendRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRecipient) {
      toast.error("Please select a recipient");
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    try {
      setLoading(true);

      const requestDoc = await addDoc(collection(db, "paymentRequests"), {
        requesterUid: senderUid,
        requestedUid: selectedRecipient.id,
        requesterUsername: senderUsername,
        requestedUsername: selectedRecipient.username,
        requesterWallet: senderWallet,
        requestedWallet: selectedRecipient.wallet,
        requesterEmail: senderEmail,
        requestedEmail: selectedRecipient.email,
        amount: parsedAmount,
        status: "pending",
        timestamp: new Date().toISOString(),
      });

      const requestParams = {
        FIRSTNAME: senderUsername || "CashTap User",
        RECIPIENT: selectedRecipient.username || "CashTap User",
        AMOUNT: parsedAmount.toFixed(2),
        REQUEST_ID: requestDoc.id,
        DATE: new Date().toISOString().split("T")[0],
        EMAIL: selectedRecipient.email || "support@cashtap.cash",
      };

      await sendEmail("send-payment-request", {
        sender_email: senderEmail || "support@cashtap.cash",
        receiver_email: selectedRecipient.email || "support@cashtap.cash",
        sender_name: senderUsername || "CashTap User",
        receiver_name: selectedRecipient.username || "CashTap User",
        params: requestParams,
      });

      toast.success("Payment request sent successfully!");
      setAmount("");
      setSearchQuery("");
      setSelectedRecipient(null);

      const sentQuery = query(
        collection(db, "paymentRequests"),
        where("requesterUid", "==", senderUid),
      );
      const sentSnapshot = await getDocs(sentQuery);
      const sentData = sentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setSentRequests(sentData);

      const receivedQuery = query(
        collection(db, "paymentRequests"),
        where("requestedUid", "==", senderUid),
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedData = receivedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setReceivedRequests(receivedData);
    } catch (err: any) {
      console.error("Error sending payment request:", err);
      toast.error("Failed to send payment request.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (request: PaymentRequest) => {
    try {
      setLoading(true);

      const requestRef = doc(db, "paymentRequests", request.id);
      await updateDoc(requestRef, { status: "cancelled" });

      const cancelParams = {
        FIRSTNAME: request.requestedUsername || "CashTap User",
        CANCELLED_BY: senderUsername || "CashTap User",
        AMOUNT: request.amount.toFixed(2),
        REQUEST_ID: request.id,
        DATE: new Date().toISOString().split("T")[0],
        EMAIL: request.requestedEmail || "support@cashtap.cash",
      };

      await sendEmail("cancel-payment-request", {
        sender_email: request.requesterEmail || "support@cashtap.cash",
        receiver_email: request.requestedEmail || "support@cashtap.cash",
        sender_name: request.requesterUsername || "CashTap User",
        receiver_name: request.requestedUsername || "CashTap User",
        params: cancelParams,
      });

      toast.success("Payment request cancelled successfully!");

      const sentQuery = query(
        collection(db, "paymentRequests"),
        where("requesterUid", "==", senderUid),
      );
      const sentSnapshot = await getDocs(sentQuery);
      const sentData = sentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setSentRequests(sentData);

      const receivedQuery = query(
        collection(db, "paymentRequests"),
        where("requestedUid", "==", senderUid),
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedData = receivedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setReceivedRequests(receivedData);
    } catch (err: any) {
      console.error("Error cancelling payment request:", err);
      toast.error("Failed to cancel payment request.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPayModal = (request: PaymentRequest) => {
    const parsedAmount = request.amount;
    const fee = parsedAmount * 0.01;
    const totalAmount = parsedAmount + fee;
    if (totalAmount > parseFloat(walletBalance)) {
      toast.error("Insufficient USD balance for amount + fee");
      return;
    }
    setSelectedPayRequest(request);
    setShowPayModal(true);
  };

  const confirmPayRequest = async () => {
    if (!selectedPayRequest) return;

    try {
      setLoading(true);

      if (!wallet) {
        throw new Error("Wallet not loaded");
      }

      const feeAmount = selectedPayRequest.amount * 0.01;
      const USDC_TOKEN_LOCATOR = "usdc";

      const { hash: transferHash } = await wallet.send(
        selectedPayRequest.requesterWallet,
        USDC_TOKEN_LOCATOR,
        selectedPayRequest.amount.toString(),
      );

      const adminWallet = import.meta.env.VITE_Admin_Wallet;
      if (adminWallet) {
        try {
          await wallet.send(
            adminWallet,
            USDC_TOKEN_LOCATOR,
            feeAmount.toString(),
          );
          // Notify admin about fee received
          fetch(`${import.meta.env.VITE_SERVERURL}/notify-admin-fee`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
            },
            body: JSON.stringify({
              username: senderUsername,
              recipientUsername: selectedPayRequest.requesterUsername || "",
              senderWallet: senderWallet,
              recipientWallet: selectedPayRequest.requesterWallet,
              amount: selectedPayRequest.amount,
              fee: feeAmount,
              txHash: transferHash,
            }),
          }).catch(() => {});
        } catch (feeErr: any) {
          console.error("Fee transfer failed:", feeErr);
        }
      }

      const txHash = transferHash;

      const requestRef = doc(db, "paymentRequests", selectedPayRequest.id);
      await updateDoc(requestRef, { status: "completed" });

      const transactionDate = new Date().toISOString().split("T")[0];
      const transactionParams = {
        AMOUNT: selectedPayRequest.amount.toFixed(2),
        SENDER: senderUsername || "CashTap User",
        TRANSFERED_TO: selectedPayRequest.requesterUsername || "CashTap User",
        TRANSACTION_ID: txHash,
        DATE: transactionDate,
      };

      await sendEmail("complete-transfer", {
        sender_email: senderEmail || "support@cashtap.cash",
        receiver_email:
          selectedPayRequest.requesterEmail || "support@cashtap.cash",
        sender_name: senderUsername || "CashTap User",
        receiver_name: selectedPayRequest.requesterUsername || "CashTap User",
        transaction_details: transactionParams,
      });

      toast.success("Payment completed successfully!");

      const usdcBal = await getBalance(senderWallet);
      setWalletBalance(usdcBal);

      const receivedQuery = query(
        collection(db, "paymentRequests"),
        where("requestedUid", "==", senderUid),
      );
      const receivedSnapshot = await getDocs(receivedQuery);
      const receivedData = receivedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as PaymentRequest[];
      setReceivedRequests(receivedData);

      setShowPayModal(false);
      setSelectedPayRequest(null);
    } catch (err: any) {
      console.error("Error completing payment:", err.message);
      let errorMessage = "Failed to complete payment: " + err.message;

      if (
        err.message.includes("insufficient funds") ||
        err.message.includes("Insufficient balance")
      ) {
        errorMessage = "Insufficient test USD balance.";
      } else if (err.message.includes("wallet")) {
        errorMessage = "Wallet error. Please ensure your wallet is connected.";
      }

      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4 sm:p-0">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          Payment Requests
        </h1>
        <p className="text-gray-400 text-lg">Manage your payment requests</p>
      </div>

      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setActiveTab("send")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            activeTab === "send"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Send Request
        </button>
        <button
          onClick={() => setActiveTab("myRequests")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            activeTab === "myRequests"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          My Requests
        </button>
        <button
          onClick={() => setActiveTab("received")}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            activeTab === "received"
              ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Received Requests
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {activeTab === "send" && (
        <form onSubmit={handleSendRequest} className="space-y-6">
          <div className="bg-gradient-to-br z-20 from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm relative">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                Select User to Request From
              </h3>
              <button
                type="button"
                onClick={() => setShowFavoritesModal(true)}
                className="group relative flex items-center gap-2 px-5 py-2.5 text-yellow-200 font-medium transition-all duration-300"
              >
                <div className="relative">
                  <Star
                    size={20}
                    className="text-yellow-400/90 group-hover:text-yellow-300 transition-colors"
                  />
                </div>
                My Favorites
              </button>
            </div>

            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all font-mono"
                placeholder="Enter CashTapTag, Phone, Email"
                required
              />
              {showDropdown && filteredResults.length > 0 && (
                <div className="absolute w-full z-[10000] top-full mt-2 bg-gradient-to-br from-[#1A2A3B] to-[#0D1419] border border-gray-700/50 rounded-3xl shadow-[0_0_20px_rgba(97,206,112,0.15)] max-h-72 overflow-y-auto">
                  {filteredResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectRecipient(item)}
                      className="p-4 hover:bg-[#61CE70]/5 cursor-pointer flex items-center justify-between border-b border-gray-700/50 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-gray-600/50">
                          {item.photoURL ? (
                            <img
                              src={item.photoURL}
                              alt={item.username || "User"}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                                (
                                  e.target as HTMLImageElement
                                ).parentElement!.innerHTML = `
                                  <div class="w-full h-full bg-gray-700 flex items-center justify-center">
                                    <Users class="w-5 h-5 text-gray-400" />
                                  </div>
                                `;
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-[#61CE70]/20 flex items-center justify-center">
                              <Users className="w-5 h-5 text-[#61CE70]" />
                            </div>
                          )}
                        </div>
                        <div>
                          <span className="text-white font-medium">
                            {item.username}
                          </span>
                          <p className="text-gray-400 text-xs">
                            {maskContact(item.email) ||
                              maskContact(item.phone) ||
                              "No contact"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-500 text-sm font-mono">
                          {truncateAddress(item.wallet)}
                        </span>
                        {selectedRecipient?.id === item.id && (
                          <Check className="w-5 h-5 text-[#61CE70]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {selectedRecipient && (
                <div className="mt-2 p-3 bg-gray-800/30 rounded-xl border border-[#61CE70]/20 flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-[#61CE70]" />
                  <p className="text-gray-300 text-sm">
                    Recipient Wallet:{" "}
                    <span className="font-mono text-white">
                      {truncateAddress(selectedRecipient.wallet)}
                    </span>
                  </p>
                </div>
              )}
              {selectedRecipient && !isFavorited && (
                <button
                  type="button"
                  onClick={addToFavorites}
                  className="group mt-4 flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-medium text-sm transition-colors duration-300 relative"
                >
                  <Star
                    size={18}
                    className="text-yellow-500/90 group-hover:text-yellow-400 transition-colors"
                  />
                  Add to Favorites
                </button>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-4">Amount</h3>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white text-lg placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                placeholder="0.0"
                min="0.1"
                step="0.1"
                required
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                USD
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={!amount || !selectedRecipient || loading}
            className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-4 rounded-2xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center space-x-2"
          >
            <Send className="w-5 h-5" />
            <span>{loading ? "Sending..." : "Send Payment Request"}</span>
          </button>
        </form>
      )}

      {activeTab === "myRequests" && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-6">
            My Payment Requests
          </h3>
          {sentRequests.length === 0 ? (
            <div className="text-center text-gray-400">
              No sent requests found.
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {sentRequests.map((req) => (
                <div
                  key={req.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-gray-600/50">
                      {req.targetPhotoURL ? (
                        <img
                          src={req.targetPhotoURL}
                          alt={req.requestedUsername}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <Users className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">
                        To: {req.requestedUsername}
                      </p>
                      <p className="text-gray-400 text-sm">
                        Amount: {req.amount.toFixed(2)} USD
                      </p>
                      <p className="text-gray-500 text-xs">{req.timestamp}</p>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                    <p
                      className={`text-base sm:text-lg font-bold ${
                        req.status === "completed"
                          ? "text-[#61CE70]"
                          : req.status === "cancelled"
                            ? "text-red-400"
                            : "text-yellow-400"
                      }`}
                    >
                      {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                    </p>
                    {req.status === "pending" && (
                      <button
                        onClick={() => handleCancelRequest(req)}
                        disabled={loading}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? "Cancelling..." : "Cancel Request"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "received" && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-6">
            Payment Requests Received
          </h3>
          {receivedRequests.length === 0 ? (
            <div className="text-center text-gray-400">
              No received requests found.
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {receivedRequests.map((req) => (
                <div
                  key={req.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-gray-600/50">
                      {req.targetPhotoURL ? (
                        <img
                          src={req.targetPhotoURL}
                          alt={req.requesterUsername}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <Users className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">
                        From: {req.requesterUsername}
                      </p>
                      <p className="text-gray-400 text-sm">
                        Amount: {req.amount.toFixed(2)} USD
                      </p>
                      <p className="text-gray-500 text-xs">{req.timestamp}</p>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                    {req.status === "pending" ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleOpenPayModal(req)}
                          disabled={loading}
                          className="bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white px-4 py-2 rounded-xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? "Processing..." : "Pay Now"}
                        </button>
                        <button
                          onClick={() => handleCancelRequest(req)}
                          disabled={loading}
                          className="bg-red-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? "Cancelling..." : "Cancel Request"}
                        </button>
                      </div>
                    ) : (
                      <p
                        className={`text-base sm:text-lg font-bold ${
                          req.status === "completed"
                            ? "text-[#61CE70]"
                            : "text-red-400"
                        }`}
                      >
                        {req.status.charAt(0).toUpperCase() +
                          req.status.slice(1)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showFavoritesModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="sticky top-0 bg-[#0F1419] rounded-3xl border-b border-gray-700 p-6 flex items-center justify-between z-10">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Star className="text-[#61CE70]" size={28} />
                My Favorites
              </h3>
              <button
                onClick={() => setShowFavoritesModal(false)}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              >
                <X size={28} className="text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="p-6 border-b border-gray-700">
              <div className="relative">
                <input
                  type="text"
                  value={favoritesSearch}
                  onChange={(e) => setFavoritesSearch(e.target.value)}
                  placeholder="Search by name, wallet, email..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-800/40 border border-gray-700/70 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/40 outline-none transition-all"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide">
              {favorites.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Star className="w-12 h-12 mx-auto mb-4 opacity-40" />
                  <p className="text-lg">No favorites yet</p>
                  <p className="mt-2">
                    Add users to favorites for quick payment requests
                  </p>
                </div>
              ) : filteredFavorites.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-lg">No matching favorites</p>
                  <p className="mt-2">Try a different search term</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFavorites.map((fav) => {
                    const match = users.find(
                      (r) =>
                        (r.wallet || "").toLowerCase() ===
                        (fav.wallet || "").toLowerCase(),
                    );
                    return (
                      <div
                        key={fav.id}
                        className="flex items-center justify-between p-4 bg-gray-900/60 rounded-2xl border border-gray-800 hover:border-gray-700 transition-all cursor-pointer group"
                        onClick={() => selectFromFavorites(fav)}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-gray-600/50">
                            {match?.photoURL ? (
                              <img
                                src={match.photoURL}
                                alt={fav.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display =
                                    "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-[#61CE70]/20 flex items-center justify-center">
                                <Users className="w-6 h-6 text-[#61CE70]" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-white truncate">
                              {fav.name}
                            </p>
                            <p className="text-sm text-gray-400 truncate">
                              {maskContact(fav.email) ||
                                maskContact(fav.phone) ||
                                truncateAddress(fav.wallet)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedToEdit(fav);
                              setEditName(fav.name);
                              setShowEditFavoriteModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-[#61CE70] transition-colors"
                          >
                            <Pencil size={20} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFavorite(fav.id);
                            }}
                            className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddFavoriteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-md p-6">
            <h3 className="text-2xl font-bold text-white mb-6">
              Add to Favorites
            </h3>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] transition-all"
              placeholder="Enter Nick name"
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowAddFavoriteModal(false)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-2xl transition-colors text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddFavorite}
                className="flex-1 py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/30 transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditFavoriteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-md p-6">
            <h3 className="text-2xl font-bold text-white mb-6">
              Edit Favorite
            </h3>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] transition-all"
              placeholder="Enter new name"
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowEditFavoriteModal(false)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-2xl transition-colors text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmEditFavorite}
                className="flex-1 py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/30 transition-all"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showPayModal && selectedPayRequest && (
        <div className="fixed z-[1000] inset-0 bg-black/70 flex items-center justify-center">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-3xl p-8 backdrop-blur-md max-w-sm md:max-w-xl w-full relative shadow-2xl shadow-[#61CE70]/10">
            <button
              onClick={() => {
                setShowPayModal(false);
                setSelectedPayRequest(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-[#61CE70] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <CheckCircle className="w-6 h-6 text-[#61CE70]" />
              <span>Confirm Payment</span>
            </h2>
            <div className="mb-5 p-3.5 bg-yellow-900/20 border border-yellow-600/30 rounded-xl text-yellow-300 text-sm leading-relaxed">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-4.5 h-4.5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <p>
                  By continuing, you confirm this transfer is authorized by you
                  and is final. Transfers are{" "}
                  <strong className="text-yellow-200">irreversible</strong> —
                  CashTap cannot hold, cancel or reverse completed transfers.{" "}
                  <strong className="text-yellow-200 mt-1">
                    Beware of scams.
                  </strong>
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Wallet className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">From:</span>
                </div>
                <span className="text-white font-medium">
                  CashTap Wallet (
                  <span className="text-[#61CE70]"> {senderUsername} </span>)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">To:</span>
                </div>
                <span className="text-white font-medium">
                  {selectedPayRequest.requesterUsername} (CashTap User)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">Payment Amount:</span>
                </div>
                <span className="text-white">
                  {selectedPayRequest.amount.toFixed(2)} USD
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">Fees (1%):</span>
                </div>
                <span className="text-white">
                  {(selectedPayRequest.amount * 0.01).toFixed(2)} USD
                </span>
              </div>
              <div className="border-t border-[#61CE70]/20 pt-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-white font-medium">Total Amount:</span>
                </div>
                <span className="text-[#61CE70] font-bold">
                  {(selectedPayRequest.amount * 1.01).toFixed(2)} USD
                </span>
              </div>
            </div>
            <div className="mt-8 flex w-full justify-center space-x-2">
              <button
                onClick={() => {
                  setShowPayModal(false);
                  setSelectedPayRequest(null);
                }}
                className="w-1/2 text-center justify-center py-3 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-all flex items-center space-x-2"
              >
                <X className="w-4 h-4 bg-white text-red-500 rounded-lg" />
                <span>Cancel</span>
              </button>
              <button
                onClick={() => {
                  setShowPayModal(false);
                  confirmPayRequest();
                }}
                disabled={loading}
                className="w-1/2 justify-center py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>{loading ? "Processing..." : "Confirm"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRequestPage;
